import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Children } from '../../decorators/child.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { UICurtain } from './ui-curtain';
import type { UICurtainsLayout, UICurtainsLayouts } from './ui-curtain.type';
import { EventHandler } from '../../global/utils.type';
import { CMS_CONTENT_MODES } from '../../cms';
import styles from './ui-curtains.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICurtains
 * @element ui-curtains
 * @classdesc Represents a class for <code>ui-curtains</code> element.
 * Container for ui-curtain elements.
 * @listens event:curtain-toggle
 * @property {boolean} [autoclose=false] {@attr autoclose} If true, curtain will
 * close automatically, when other curtain inside ui-curtains is opened.
 * @property {boolean} [autoscroll=false] {@attr autoscroll} Enables for each curtain in ui-curtains - scroll.
 * @property {UICurtainsLayouts} [layout] {@attr layout} Layout of curtains.
 * @property {number} [startIndex=1] {@attr start-index} Start index for numbered curtains
 * @slot {@type "ui-curtain"}
 * @example
 * <ui-curtains layout="numbered">
 *   <ui-curtain label="Label for 1st">Content 1</ui-curtain>
 * </ui-curtains>
 */
@Component({
    tag: 'ui-curtains',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
        allowedChildren: ['ui-curtain'],
    },
})
export class UICurtains extends UIElement {
    static readonly Layout: UICurtainsLayouts = {
        Default: 'default',
        Numbered: 'numbered',
    } as const;

    /**
     * @type {Props}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['autoclose'];
    }

    @Attribute(Boolean) autoclose?: boolean;
    @Attribute(Boolean) autoscroll?: boolean;
    @Attribute(String) layout?: UICurtainsLayout;
    @Attribute(Number) startIndex: number = 1;

    @Children(':scope > ui-curtain')
    curtains: Array<UICurtain> = [];

    private handleCurtainToggleBound?: EventHandler;

    /**
     * Is curtain inside this wrapper - numbered/completed
     * @returns {boolean}
     */
    isNumbered(): boolean {
        return (
            this.layout === UICurtains.Layout.Numbered ||
            !!this.querySelector(
                ':scope > ui-curtain[index], :scope > ui-curtain[completed]'
            )
        );
    }

    /**
     * Initializes a curtain in ui-curtains
     * - Sets role to `listitem`
     * - Sets the index
     * - Sets curtain auto-scrolled after opening
     * @param {UICurtain} curtain
     * @param {number} index
     * @private
     */
    private initCurtain(curtain: UICurtain, index: number): void {
        curtain.setAttribute('role', 'listitem');

        if (this.layout === UICurtains.Layout.Numbered) {
            curtain.setAttribute('index', String(this.startIndex + index));
        }

        if (this.autoscroll) {
            curtain.setAttribute('autoscroll', '');
        }
    }

    /**
     * Close each curtain except first if 'autoclose' functionality is switched on.
     */
    prepareAutocloseCurtains(): void {
        Array.from(
            this.queryChildren('ui-curtain[open] ~ ui-curtain[open]')
        ).forEach((element) => ((element as UICurtain).open = false));
        this.addEventListener('curtain-toggle', this.handleCurtainToggleBound!);
    }

    /**
     * Fires callback when curtain-toggle event will bubble.
     * @param {UIEvent} e
     * @private
     */
    private handleCurtainToggle(e: Event): void {
        if (this !== (e.target as Element).closest('ui-curtains')) {
            return;
        }

        Array.from(this.queryChildren('ui-curtain[open]')).forEach((target) => {
            if (target !== e.target) {
                (target as UICurtain).open = false;
            }
        });
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: string | null
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'autoclose':
                if (newValue === null || !this.isNumbered()) {
                    this.removeEventListener(
                        'curtain-toggle',
                        this.handleCurtainToggleBound!
                    );
                    return;
                }
                this.prepareAutocloseCurtains();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.setAttribute('role', 'list');
        this.curtains.forEach(this.initCurtain.bind(this));
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.handleCurtainToggleBound = this.handleCurtainToggle.bind(this);
        if (this.autoclose && this.isNumbered()) {
            this.addEventListener(
                'curtain-toggle',
                this.handleCurtainToggleBound
            );
        }
    }
}
